import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    Signal,
    signal,
    WritableSignal
} from "@angular/core";
import {provideSearchModel} from "@fedelta-pos/data-client";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {Page} from "../../navigation/page";
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {IdentityModel} from '../../identity/identity.model';
import {MemberSalesData, MemberSalesSearchModel} from '../../identity/member-sales-search.model';
import {MemberRecentSaleData} from '../../identity/member-summary-entity.model';
import {OrderType} from '../../order/order';
import {OrderModel} from '../../order/order.model';
import {RecentOrderData, RecentOrderService} from '../../order/recent-order.service';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {StoreData} from '../../store/store-search.model';
import {TranslateModule} from '@ngx-translate/core';
import {DatePipe} from '@angular/common';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {FieldGroupComponent} from '../../inputs/form/field-group/field-group.component';
import {LinkComponent} from '../../buttons/link/link.component';
import {HtmlPipe} from '../../utils/html.pipe';
import {CurrencyPipe} from '../../utils/currency.pipe';
import {PossessivePipe} from '../../utils/possessive.pipe';
import {ConfigurationModel} from '../../configuration/configuration.model';

@Component({
    selector: "woa-customer-orders-page",
    templateUrl: "./customer-orders-page.component.html",
    providers: [
        {provide: Page, useExisting: CustomerOrdersPageComponent},
        provideSearchModel(MemberSalesSearchModel)
    ],
    styleUrl: "./customer-orders-page.component.scss",
    imports: [
        ContentMessageComponent,
        CurrencyPipe,
        DatePipe,
        FieldGroupComponent,
        HtmlPipe,
        LinkComponent,
        ModalPageLayoutComponent,
        PossessivePipe,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerOrdersPageComponent extends Page implements OnInit {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public memberSalesSearchModel: MemberSalesSearchModel = inject(MemberSalesSearchModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    public orderModel: OrderModel = inject(OrderModel);
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    private recentOrderService: RecentOrderService = inject(RecentOrderService);
    private loadingService: LoadingService = inject(LoadingService);

    MessageLevel = MessageLevel;
    OrderType = OrderType;

    public readonly recentOrders: WritableSignal<RecentOrderData[]> = signal<RecentOrderData[]>([]);

    public readonly saleCount: Signal<number> = computed<number>(() =>
        this.recentOrders().length + (this.memberSalesSearchModel.dataSignal()?.length ?? 0));

    ngOnInit(): void {

        void this.loadingService.performWithSpinner(async () => {
            if (this.identityModel.currentMember() && !this.identityModel.isGuestUser()) {
                try {
                    await this.memberSalesSearchModel.retrieve(10000);
                } catch (error) {
                    console.error("Failed to retrieve member sales", error);
                }
                this.recentOrderService.purgeOldOrders(this.identityModel.currentMember().recentSales.map((recentSale: MemberRecentSaleData) => recentSale.orderLookupCode));
            }
            this.recentOrders.set(this.recentOrderService.recentOrders.filter((recentOrder: RecentOrderData) => !recentOrder.convertedToSale));
        }, undefined, "woa.message.orders.loading");
    }

    getOrderDescription(recentOrder: RecentOrderData): string {
        return "woa.common.order." +
            (recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "ahead"
                : recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_DELIVERY ? "delivery"
                    : recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? "atTable" : "") +
            ".order.noun";
    }

    getOrderMessage(recentOrder: RecentOrderData, store: StoreData): string {
        const canShowEta: boolean = this.orderingContextModel.canShowETAForStore(store);
        const etaPast: boolean = recentOrder.placedOrder.estimatedCompletionDateTime && recentOrder.placedOrder.estimatedCompletionDateTime < new Date();
        return recentOrder.orderInitiation.transferTargetId ? "woa.message.order.summary.transferredToGroupOrder"
            : recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? "woa.message.order.summary.orderPlaced"
                : !canShowEta ? (
                        etaPast ? (recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.message.order.summary.asapPickupTimePast" : "woa.message.order.summary.asapDeliveryTimePast")
                            : (recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.message.order.summary.asapPickupTime" : "woa.message.order.summary.asapDeliveryTime")
                    )
                    : recentOrder.initiatedOrder.orderLaterDateTime ? (recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.message.order.summary.scheduledPickupTime" : "message.order.summary.scheduledDeliveryTime")
                        : etaPast ? (recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.message.order.summary.estimatedPickupTimePast" : "woa.message.order.summary.estimatedDeliveryTimePast")
                            : (recentOrder.orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.message.order.summary.estimatedPickupTime" : "woa.message.order.summary.estimatedDeliveryTime");
    }

    getOrderTime(recentOrder: RecentOrderData): string {
        return !recentOrder.initiatedOrder.orderLaterDateTime ? recentOrder.placedOrder.estimatedCompletionTime
            : recentOrder.placedOrder && recentOrder.placedOrder.estimatedCompletionTime ? this.orderModel.formatEstimatedCompletionDateTime(recentOrder.placedOrder)
                : this.orderModel.formatRequiredDateTime(recentOrder.initiatedOrder);
    }

    getSaleDescription(sale: MemberSalesData): string {
        return "woa.label.sale-summary.sale-description." +
            (sale.storeId ? "online" : "inStore") +
            (sale.saleType === MemberSalesData.DELIVERY ? "Delivery"
                : sale.saleType === MemberSalesData.TAKEAWAY ? "Takeaway"
                    : sale.saleType === MemberSalesData.APP_AT_TABLE ? "AppAtTable"
                        : "Order");
    }

    async viewOrderDetails(recentOrder: RecentOrderData): Promise<void> {
        return this.navigationService.createPage("woa-order-details-page", {recentOrder});
    }

    async viewReceipt(sale: MemberSalesData): Promise<void> {
        return this.navigationService.createPage("woa-sale-receipt-page", {sale: sale});
    }

    async requestInvoice(sale: MemberSalesData): Promise<void> {
        return this.navigationService.createPage("woa-invoice-request-page", {sale: sale});
    }
}
